// cart-drawer.js
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('.cart-drawer__dialog');
    document.addEventListener('cart:open', () => this.open());
    document.addEventListener('cart:refresh', () => this.refresh());
    this.querySelector('[data-cart-close]')?.addEventListener('click', () => this.close());
    this.dialog?.addEventListener('click', (e) => { if (e.target === this.dialog) this.close(); });
    this.addEventListener('click', (e) => this.onQty(e));
    this.addEventListener('submit', (e) => this.onDiscount(e));
    this.initRecDrag();
  }
  // Drag-to-scroll for the "You may also like" rail. Delegated on the persistent
  // cart-drawer element so it keeps working after the body is re-rendered.
  initRecDrag() {
    this.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'touch') return;
      const track = e.target.closest('[data-cart-recs]');
      if (!track) return;
      this._rec = { track, x: e.clientX, left: track.scrollLeft };
      this._recMoved = false;
      track.classList.add('cursor-grabbing');
    });
    window.addEventListener('pointermove', (e) => {
      if (!this._rec) return;
      const dx = e.clientX - this._rec.x;
      if (Math.abs(dx) > 3) this._recMoved = true;
      this._rec.track.scrollLeft = this._rec.left - dx;
    });
    window.addEventListener('pointerup', () => {
      if (!this._rec) return;
      this._rec.track.classList.remove('cursor-grabbing');
      this._rec = null;
    });
    this.addEventListener('click', (e) => {
      if (this._recMoved && e.target.closest('[data-cart-recs]')) { e.preventDefault(); e.stopPropagation(); this._recMoved = false; }
    }, true);
  }
  open() {
    if (!this.dialog) return;
    this.dialog.showModal();
    document.body.style.overflow = 'hidden';
    // next frame: add is-open so the CSS transform transition plays (slide in)
    requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
  }
  close() {
    if (!this.dialog) return;
    this.classList.remove('is-open');
    document.body.style.overflow = '';
    const d = this.dialog;
    let done = false;
    const finish = () => { if (done) return; done = true; d.close(); };
    d.addEventListener('transitionend', finish, { once: true });
    setTimeout(finish, 500); // fallback (reduced motion / no transition)
  }
  async onQty(e) {
    const bouquetRemove = e.target.closest('[data-bouquet-remove]');
    if (bouquetRemove) { e.preventDefault(); return this.removeBouquet(bouquetRemove.getAttribute('data-bouquet-remove')); }
    const remove = e.target.closest('[data-cart-remove]');
    const btn = e.target.closest('[data-qty-up],[data-qty-down]');
    if (!remove && !btn) return;
    const li = (remove || btn).closest('.cart-item');
    if (!li) return;
    let q;
    if (remove) {
      q = 0;
    } else {
      const input = li.querySelector('[data-qty-input]');
      q = Math.max(0, parseInt(input.value, 10) + (btn.hasAttribute('data-qty-up') ? 1 : -1));
    }
    const r = await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: li.dataset.key, quantity: q, sections: 'cart-drawer,header' }) });
    if (!r.ok) return;
    const data = await r.json();
    if (data.status) return;
    this.renderSections(data.sections);
    // Announce it. This is the path that lowers the cart total, and for a long
    // time it was the only cart write that told nobody: the free-gift ladder
    // never learned the cart had dropped, so a gift stayed free below its
    // threshold. The ladder also watches the network as a backstop, but saying
    // it out loud is what makes the behaviour deterministic.
    document.dispatchEvent(new CustomEvent('cart:changed'));
  }
  // Remove every line that belongs to one bouquet in a single update.
  async removeBouquet(bid) {
    if (!bid) return;
    const keys = Array.from(this.querySelectorAll('.cart-item'))
      .filter((li) => li.dataset.bouquet === bid)
      .map((li) => li.dataset.key);
    if (!keys.length) return;
    const updates = {};
    keys.forEach((k) => { updates[k] = 0; });
    const r = await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates, sections: 'cart-drawer,header' }) });
    if (!r.ok) return;
    const data = await r.json();
    this.renderSections(data.sections);
    document.dispatchEvent(new CustomEvent('cart:changed'));
  }
  async onDiscount(e) {
    const form = e.target.closest('[data-discount-form]');
    if (!form) return;
    e.preventDefault();
    const input = form.querySelector('[name=discount]');
    const code = (input?.value || '').trim();
    const msg = this.querySelector('[data-discount-msg]');
    const btn = form.querySelector('[data-discount-apply]');
    if (!code) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const setMsg = (text, ok) => {
      if (!msg) return;
      msg.textContent = text;
      msg.classList.remove('hidden', 'text-red', 'text-forest');
      msg.classList.add(ok ? 'text-forest' : 'text-red');
    };
    const money = (cents) => {
      try { return '₹' + (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 }); }
      catch (e) { return '₹' + Math.round(cents / 100); }
    };
    btn?.setAttribute('aria-busy', 'true');
    setMsg('Applying…', true);
    try {
      // Set the storefront cart-level discount cookie (Shopify applies it to the cart).
      await fetch(root + 'discount/' + encodeURIComponent(code)).catch(() => {});
      // Read the cart back to reflect any savings, then re-render the drawer.
      const cart = await (await fetch(root + 'cart.js')).json();
      this.refresh();
      if (cart.total_discount > 0) setMsg('Code “' + code + '” applied — you saved ' + money(cart.total_discount) + '.', true);
      else setMsg('Code “' + code + '” applied. Discount shows at checkout.', true);
      if (input) input.value = '';
    } catch (err) {
      setMsg('Could not apply that code. Please try again.', false);
    } finally {
      btn?.removeAttribute('aria-busy');
    }
  }
  refresh() {
    fetch('/?sections=cart-drawer,header')
      .then((r) => r.json())
      .then((s) => this.renderSections(s))
      .catch(() => {});
  }
  renderSections(sections) {
    if (!sections) return;
    if (sections['cart-drawer']) {
      const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const next = parsed.querySelector('[data-cart-body]');
      const foot = parsed.querySelector('[data-cart-foot]');
      const existingBody = this.querySelector('[data-cart-body]');
      const existingFoot = this.querySelector('[data-cart-foot]');
      if (next && existingBody) existingBody.replaceWith(next);
      if (foot && existingFoot) existingFoot.replaceWith(foot);
    }
    if (sections['header']) {
      const count = new DOMParser().parseFromString(sections['header'], 'text/html').querySelector('[data-cart-count]');
      if (count) document.querySelector('[data-cart-count]')?.replaceWith(count);
    }
  }
}
customElements.define('cart-drawer', CartDrawer);
window.refreshCartSections = (s) => document.querySelector('cart-drawer')?.renderSections(s);

// The /cart page renders the same cart-line-items markup as the drawer, but the
// handlers above are bound to the <cart-drawer> element and never see a click on
// the page, so quantity, remove and bouquet-remove did nothing there at all.
// Delegated on document, scoped to [data-cart-page], and re-rendering the `main`
// section in place so the page stays ajax like the drawer.
(function () {
  const cartRoot = () => (window.Shopify?.routes?.root) || '/';

  function paint(sections) {
    if (!sections) return;
    if (sections.main) {
      const cur = document.querySelector('[data-cart-page]');
      const next = new DOMParser().parseFromString(sections.main, 'text/html').querySelector('[data-cart-page]');
      if (cur && next) cur.replaceWith(next);
    }
    window.refreshCartSections?.(sections);
  }

  async function apply(updates) {
    if (!Object.keys(updates).length) return;
    const r = await fetch(`${cartRoot()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, sections: 'main,cart-drawer,header' })
    });
    if (!r.ok) return;
    const data = await r.json();
    paint(data.sections);
    document.dispatchEvent(new CustomEvent('cart:changed'));
  }

  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-cart-page]')) return;

    const bouquet = e.target.closest('[data-bouquet-remove]');
    if (bouquet) {
      e.preventDefault();
      const bid = bouquet.getAttribute('data-bouquet-remove');
      const updates = {};
      document.querySelectorAll('[data-cart-page] .cart-item').forEach((li) => {
        if (li.dataset.bouquet === bid) updates[li.dataset.key] = 0;
      });
      return apply(updates);
    }

    const remove = e.target.closest('[data-cart-remove]');
    const step = e.target.closest('[data-qty-up],[data-qty-down]');
    if (!remove && !step) return;
    const li = (remove || step).closest('.cart-item');
    if (!li) return;
    e.preventDefault();

    let q = 0;
    if (step) {
      const input = li.querySelector('[data-qty-input]');
      q = Math.max(0, parseInt(input?.value, 10) + (step.hasAttribute('data-qty-up') ? 1 : -1));
      if (Number.isNaN(q)) return;
    }
    apply({ [li.dataset.key]: q });
  });

  // typing a quantity straight into the box
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-qty-input]');
    if (!input || !input.closest('[data-cart-page]')) return;
    const li = input.closest('.cart-item');
    if (!li) return;
    const q = Math.max(0, parseInt(input.value, 10));
    if (Number.isNaN(q)) return;
    apply({ [li.dataset.key]: q });
  });
})();
